import { useDidMount } from "@better-hooks/lifecycle";
import { Link } from "@tanstack/react-router";
import { ErrorComponentProps } from "@tanstack/router-core";
import { RefreshCw, AlertTriangle, ArrowLeft } from "lucide-react";

import { Meteors } from "@/components/magicui/meteors";
import { Button } from "@/components/ui/button";
import { m } from "@/paraglide/messages.js";
import { appConfig } from "@complete-athlete/enums";

export const AppError = ({ error, info, reset }: ErrorComponentProps) => {
  useDidMount(() => {
    console.error(error, info);
  });

  return (
    <div className="h-full bg-zinc-900 text-zinc-100 flex items-center justify-center p-4">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <Meteors number={30} />
      </div>
      <div className="max-w-2xl w-full space-y-6">
        <div className="relative">
          <div className="relative space-y-2 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-red-400" />
              </div>
            </div>
            <h1 className="pointer-events-none whitespace-pre-wrap bg-gradient-to-b from-zinc-600 to-zinc-300/80 bg-clip-text text-center text-7xl font-semibold leading-none text-transparent dark:from-white dark:to-zinc-900/10 pb-2">
              {m.error_app_title()}
            </h1>
            <p className="text-xl text-zinc-400 mt-8">{error.message || m.error_fallback_message()}</p>
          </div>
        </div>

        <div className="rounded-lg p-6 space-y-6">
          <div className="pt-4 flex justify-center gap-2">
            <Button variant="secondary" asChild>
              <Link to="/">
                <ArrowLeft className="w-4 h-4" />
                {m.not_found_cta()}
              </Link>
            </Button>
            <Button variant="ghost" onClick={reset}>
              <RefreshCw className="w-4 h-4" />
              {m.error_refresh()}
            </Button>
          </div>
        </div>

        <p className="text-sm text-zinc-500 text-center">
          {m.error_report_prefix()}{" "}
          <a
            href={`https://github.com/${appConfig.repository.owner}/${appConfig.repository.name}/issues`}
            target="_blank"
            className="text-blue-500 hover:text-blue-400 transition-colors"
            rel="noreferrer"
          >
            {m.error_report_link()}
          </a>
          .
        </p>
      </div>
    </div>
  );
};
